<template>
  <div class="avatar-upload-wrapper">
    <div class="avatar-upload-left">
      <div class="avatar-preview-container" @click="triggerUpload" :style="{ width: size + 'px', height: size + 'px' }">
        <el-avatar 
          v-if="value" 
          :size="size" 
          :src="getAvatarUrl(value)" 
          shape="square"
          class="avatar-preview"
          @error="handleImageError"
        >
          <img src="https://cube.elemecdn.com/e/fd/0fc7d20532fdaf769a25683617711png.png"/>
        </el-avatar>
        <div v-else class="avatar-placeholder">
          <i class="el-icon-plus"></i>
          <span>{{ $t('userManagement.uploadAvatar') }}</span>
        </div>
        <div class="avatar-mask" v-if="value">
          <i class="el-icon-camera"></i>
        </div>
      </div>
      <div class="avatar-tip">{{ $t('userManagement.avatarSizeTip') }}</div>
    </div>

    <div class="avatar-defaults-right">
      <div class="defaults-title">{{ $t('userManagement.selectDefaultAvatar') }}</div>
      <div class="default-avatars-grid">
        <div 
          v-for="n in 5" 
          :key="n" 
          class="default-avatar-item"
          :class="{ active: isDefaultSelected(n) }"
          @click="selectDefaultAvatar(n)"
        >
          <el-avatar :size="40" :src="getDefaultAvatarUrl(n)" shape="square"></el-avatar>
        </div>
      </div>
    </div>

    <!-- Hidden file input -->
    <input
      type="file"
      ref="fileInput"
      style="display: none"
      accept="image/*"
      @change="handleFileChange"
    />

    <!-- Cropping Dialog -->
    <el-dialog
      :title="$t('userManagement.cropAvatar')"
      :visible.sync="dialogVisible"
      width="600px"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleDialogClosed"
    >
      <div class="cropper-wrapper">
        <vue-cropper
          ref="cropper"
          :img="cropperImg"
          :outputSize="1"
          outputType="png"
          :info="true"
          :full="true"
          :canMove="true"
          :canMoveBox="true"
          :original="false"
          :autoCrop="true"
          :autoCropWidth="128"
          :autoCropHeight="128"
          :fixed="true"
          :fixedNumber="[1, 1]"
          :centerBox="true"
          :infoTrue="true"
          :fixedBox="true"
        ></vue-cropper>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleCrop" :loading="cropping">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { VueCropper } from 'vue-cropper'
import { uploadAvatar } from '@/apis/user/user'
import { getFullUrl } from '@/utils/index'

export default {
  name: 'AvatarUpload',
  components: {
    VueCropper
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    size: {
      type: Number,
      default: 128
    }
  },
  data() {
    return {
      dialogVisible: false,
      cropperImg: '',
      cropping: false
    }
  },
  methods: {
    getAvatarUrl(url) {
      const fullUrl = getFullUrl(url)
      console.log('AvatarUpload - getAvatarUrl:', { input: url, output: fullUrl })
      return fullUrl
    },
    handleImageError() {
      console.warn('Avatar image load failed:', {
        value: this.value,
        url: this.getAvatarUrl(this.value)
      })
      return true
    },
    triggerUpload() {
      this.$refs.fileInput.click()
    },
    isDefaultSelected(n) {
      if (this.value === `default:${n}`) return true
      // Support legacy check
      if (this.value === `/static/avatars/user-avatar${n}.png`) return true
      return false
    },
    selectDefaultAvatar(n) {
      // Set as special value format
      const val = `default:${n}`
      this.$emit('input', val)
      this.$emit('change', val)
    },
    getDefaultAvatarUrl(n) {
      return getFullUrl(`default:${n}`)
    },
    handleFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        this.$message.error(this.$t('userManagement.selectImageTip'))
        return
      }

      const reader = new FileReader()
      reader.onload = (event) => {
        this.cropperImg = event.target.result
        this.dialogVisible = true
        // Reset input value so same file can be selected again
        e.target.value = ''
      }
      reader.readAsDataURL(file)
    },
    async handleCrop() {
      this.cropping = true
      this.$refs.cropper.getCropData(async (data) => {
        try {
          // Convert base64 to blob for upload
          const blob = this.dataURLtoBlob(data)
          const formData = new FormData()
          formData.append('file', blob, 'avatar.png')

          // Upload to backend
          const res = await uploadAvatar(formData)
          
          const avatarUrl = res
          
          this.$emit('input', avatarUrl)
          this.$emit('change', avatarUrl)
          this.dialogVisible = false
        } catch (error) {
          console.error('Avatar upload failed:', error)
          this.$message.error(this.$t('userManagement.uploadError') || '上传失败')
        } finally {
          this.cropping = false
        }
      })
    },
    dataURLtoBlob(dataurl) {
      const arr = dataurl.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new Blob([u8arr], { type: mime })
    },
    handleDialogClosed() {
      this.cropperImg = ''
    }
  }
}
</script>

<style scoped lang="scss">
.avatar-upload-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
}

.avatar-upload-left {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  width: 100%;
}

.avatar-defaults-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  width: 100%;
  box-sizing: border-box;
}

.defaults-title {
  font-size: 13px;
  color: #909399;
  font-weight: 500;
  text-align: left;
}

.default-avatars-grid {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
}

.default-avatar-item {
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 2px;
  transition: all 0.2s ease;
  display: flex;
  justify-content: center;
  background: #fff;
  
  &:hover {
    border-color: #409eff;
    transform: scale(1.05);
  }

  &.active {
    border-color: #409eff;
    background-color: #fff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
  }

  ::v-deep .el-avatar {
    border-radius: 4px;
  }
}

.avatar-preview-container {
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  border: 1px dashed #d9d9d9;
  transition: border-color 0.3s;

  &:hover {
    border-color: #409eff;
    
    .avatar-mask {
      opacity: 1;
    }
  }
}

.avatar-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f5f7fa;
  color: #909399;
  gap: 8px;
  width: 100%;
  height: 100%;

  i {
    font-size: 24px;
  }

  span {
    font-size: 12px;
  }
}

.avatar-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  font-size: 24px;
  opacity: 0;
  transition: opacity 0.3s;
}

.avatar-tip {
  font-size: 12px;
  color: #909399;
  text-align: center;
}

.cropper-wrapper {
  width: 100%;
  height: 400px;
}
</style>
